import { useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Organization, ModuleName } from '@/types';
import { TenantContext } from './tenant-context';
import { logger } from '../lib/logger';

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id;
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [activeModules, setActiveModules] = useState<ModuleName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshTenant = () => setRefreshTrigger(n => n + 1);

  useEffect(() => {
    // Mientras la auth sigue cargando no tomamos decisiones — evita el bounce
    // false → true de isLoading que hace que el Sidebar muestre todos los módulos.
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (!userId) {
      setCurrentOrganization(null);
      setActiveModules([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchTenantData = async () => {
      setIsLoading(true);
      try {
        // Obtener organization_id del perfil del usuario. maybeSingle (no
        // single): que no exista fila todavia es un caso ya manejado mas
        // abajo (sesion vieja, race de signup), no un error real -- single()
        // generaba un PGRST116 y lo mandaba a Sentry como ruido en cada uno
        // de esos casos esperados.
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userId)
          .maybeSingle();

        if (profileError) {
          logger.error('[TenantContext] Error al leer user_profiles:', profileError.message);
        }

        if (!profile?.organization_id) {
          if (!cancelled) {
            setCurrentOrganization(null);
            setActiveModules([]);
          }
          return;
        }

        // Obtener datos de la organización, salón y módulos activos en paralelo
        const [orgResult, modulesResult, salonResult] = await Promise.all([
          supabase
            .from('organizations')
            .select('*')
            .eq('id', profile.organization_id)
            .single(),
          supabase
            .from('organization_modules')
            .select('module_name')
            .eq('organization_id', profile.organization_id)
            .eq('is_active', true),
          supabase
            .from('salons')
            .select('name')
            .eq('organization_id', profile.organization_id)
            .limit(1)
            .single(),
        ]);

        if (orgResult.error) {
          logger.error('[TenantContext] Error al leer organizations:', orgResult.error.message);
        }
        if (modulesResult.error) {
          logger.error('[TenantContext] Error al leer organization_modules:', modulesResult.error.message);
        }

        if (cancelled) return;

        if (orgResult.data) {
          setCurrentOrganization(orgResult.data as Organization);
        }
        // businessName: usar nombre del salón (negocio real), fallback al nombre de la org
        setBusinessName(
          salonResult.data?.name ?? orgResult.data?.name ?? null,
        );
        // Siempre actualizar activeModules, incluso si el array viene vacío
        setActiveModules(
          (modulesResult.data ?? []).map(m => m.module_name as ModuleName),
        );
      } catch (err) {
        logger.error('[TenantContext] Error inesperado:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTenantData();

    // Cleanup: evita que una respuesta tardía actualice el estado
    // si el usuario cambió o el componente se desmontó
    return () => { cancelled = true; };
  }, [userId, authLoading, refreshTrigger]);

  const hasModule = (module: string): boolean => {
    // super_admin bypasses module checks by design —
    // no remover pensando que es un bug
    if (user?.role === 'super_admin') return true;
    // Sin organización asignada, sin acceso
    if (!currentOrganization) return false;
    return activeModules.includes(module as ModuleName);
  };

  return (
    <TenantContext.Provider value={{ currentOrganization, businessName, activeModules, hasModule, isLoading, refreshTenant }}>
      {children}
    </TenantContext.Provider>
  );
}
